/**
 * OCS Cart Toast Component
 *
 * Shows an "Added to Your Bag" notification when a product is added via
 * the OCS add-to-cart block. Listens for cart:update events with
 * source === 'product-form-component'.
 *
 * Auto-dismisses after `data-duration` seconds. Timer pauses on hover/focus.
 */
class OcsCartToastComponent extends HTMLElement {
  #timer = null;
  #productsEl = null;
  #template = null;
  #subtotalEl = null;
  /** Variant ids of the rows currently shown, in render order — used for badge hydration. */
  #pendingVariantIds = [];
  #resizeObserver = null;

  connectedCallback() {
    this.#productsEl = this.querySelector('.ocs-cart-toast__products');
    this.#template = this.querySelector('.ocs-cart-toast__product-template');
    this.#subtotalEl = this.querySelector('.ocs-cart-toast__subtotal-value');

    this.addEventListener('mouseenter', this.#pauseTimer);
    this.addEventListener('mouseleave', this.#resumeTimer);
    this.addEventListener('focusin', this.#pauseTimer);
    this.addEventListener('focusout', this.#resumeTimer);

    this.querySelector('[data-ocs-role="toast-close"]')
      ?.addEventListener('click', () => this.hide());

    this.querySelector('[data-ocs-role="toast-continue"]')
      ?.addEventListener('click', () => this.hide());

    this.querySelector('[data-ocs-role="toast-view-bag"]')
      ?.addEventListener('click', () => {
        this.hide();
        document.querySelector('ocs-cart-drawer-component')?.open();
      });

    this.#syncPosition();
    window.addEventListener('resize', this.#syncPosition, { passive: true });
    window.addEventListener('scroll', this.#syncPosition, { passive: true });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#syncPosition);
      document.querySelectorAll('#header-component, #header-component .header__row, ocs-cart-chip-component')
        .forEach((element) => this.#resizeObserver?.observe(element));
    }

    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCartUpdate);
    window.removeEventListener('resize', this.#syncPosition);
    window.removeEventListener('scroll', this.#syncPosition);
    this.#resizeObserver?.disconnect();
    this.#clearTimer();
  }

  #onCartUpdate = (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.upsellSource) return;
    const item = event.detail?.resource;
    if (!item || typeof item.price !== 'number' || !item.title) return;

    this.showForItems([item]);
  };

  /**
   * Public entry point for a single line item (bypasses event system).
   * Used by the PDP add-to-cart block and PLP quick-adds.
   * @param {Object} item - Line item from cart/add.js
   */
  showForItem(item) {
    this.showForItems([item]);
  }

  /**
   * Public entry point for one or more line items. A single add renders one row;
   * a whole-order reorder renders one row per item. The subtotal always reflects
   * the whole cart (read from the morphed drawer DOM).
   * @param {Object[]} items - Line item objects returned by cart/add.js
   */
  showForItems(items) {
    if (!this.#productsEl || !this.#template) return;

    const valid = (items || []).filter(
      (item) => item && typeof item.price === 'number' && item.title
    );
    if (!valid.length) return;

    this.#productsEl.replaceChildren();
    this.#pendingVariantIds = valid.map((item) =>
      item.variant_id ? String(item.variant_id) : null
    );

    for (const item of valid) {
      const row = this.#template.content.firstElementChild.cloneNode(true);
      this.#populateRow(row, item);
      this.#productsEl.appendChild(row);
    }

    this.#hydrateFromCart();
    this.show();
  }

  /**
   * Populate one cloned product row with data from a cart/add.js line item.
   * @param {HTMLElement} row - Cloned `.ocs-cart-toast__product` element
   * @param {Object} item - Line item object returned by cart/add.js
   */
  #populateRow(row, item) {
    // Product image (resize to ~120px via Shopify CDN transform)
    const img = row.querySelector('.ocs-cart-toast__img');
    if (img) {
      const rawUrl = item.featured_image?.url || item.image || '';
      const imgUrl = rawUrl
        ? rawUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_120x120.$1$2')
        : (window.themeSettings?.comingSoonImageUrl || '');
      img.src = imgUrl;
      img.alt = item.title || '';
    }

    // Vendor + QTY
    const vendorEl = row.querySelector('.ocs-cart-toast__vendor');
    if (vendorEl) {
      const vendorSpan = document.createElement('span');
      vendorSpan.textContent = item.vendor || '';
      const qtySpan = document.createElement('span');
      qtySpan.className = 'ocs-cart-toast__qty';
      qtySpan.textContent = 'QTY';
      vendorEl.replaceChildren(vendorSpan, qtySpan);
      vendorEl.hidden = !item.vendor;
    }

    // Title — item.title includes variant (e.g. "Product - 3.5g")
    const titleEl = row.querySelector('.ocs-cart-toast__title');
    if (titleEl) {
      const titleText = titleEl.querySelector('.ocs-cart-toast__title-text');
      const titleQty = titleEl.querySelector('.ocs-cart-toast__title-qty');
      if (titleText) titleText.textContent = item.product_title || '';
      if (titleQty) titleQty.textContent = item.quantity ?? '';
      titleEl.hidden = false;
    }

    // Options (replaces SKU)
    const optionsEl = row.querySelector('.ocs-cart-toast__options');
    if (optionsEl) {
      const sizeLabel = this.dataset.sizeLabel || 'Size';
      const opts = (item.options_with_values || [])
        .filter(o => o.value && o.value.toLowerCase() !== 'default title')
        .map(o => `${sizeLabel}: ${o.value}`)
        .join(' · ');
      optionsEl.textContent = opts;
      optionsEl.hidden = !opts;
    }

    // Unit price (cart/add.js returns price in cents)
    const priceEl = row.querySelector('.ocs-cart-toast__price');
    if (priceEl) {
      const eachLabel = this.dataset.eachLabel || 'ea';
      priceEl.textContent = `${window.OCSMoney.format(item.price)} / ${eachLabel}`;
      priceEl.hidden = false;
    }
  }

  /**
   * After the drawer re-renders (morph), copy the whole-cart subtotal and per-row
   * cannabinoid badges from the cart DOM. Runs once for the whole toast; badges
   * are matched to each rendered row by its variant id.
   */
  #hydrateFromCart() {
    setTimeout(() => {
      const drawerSubtotal = document.querySelector(
        '.ocs-cart-summary__subtotal .ocs-cart-summary__value'
      );
      if (drawerSubtotal && this.#subtotalEl) {
        this.#subtotalEl.textContent = drawerSubtotal.textContent.trim();
        this.#subtotalEl.hidden = false;
      }

      const rows = [...this.#productsEl.querySelectorAll('.ocs-cart-toast__product')];
      rows.forEach((row, index) => {
        const vid = this.#pendingVariantIds[index];
        const badgesEl = row.querySelector('.ocs-cart-toast__badges');
        if (!badgesEl || !vid) return;
        const cartItem = [...document.querySelectorAll('.ocs-cart-item')].find(
          (el) => (el.dataset.key || '').split(':')[0] === vid
        );
        const badgeContainer = cartItem?.querySelector('.ocs-cart-item__badges');
        if (badgeContainer?.children.length) {
          badgesEl.innerHTML = badgeContainer.innerHTML;
          badgesEl.removeAttribute('hidden');
        }
      });
    }, 0);
  }

  show() {
    this.#syncPosition();
    this.removeAttribute('hidden');
    this.#startTimer();
  }

  hide() {
    this.setAttribute('hidden', '');
    this.#clearTimer();
  }

  #startTimer() {
    this.#clearTimer();
    const ms = parseInt(this.dataset.duration || '5', 10) * 1000;
    this.#timer = setTimeout(() => this.hide(), ms);
  }

  #pauseTimer = () => this.#clearTimer();

  #resumeTimer = () => {
    if (!this.hasAttribute('hidden')) this.#startTimer();
  };

  #syncPosition = () => {
    const isVisible = (element) => {
      if (!(element instanceof Element)) return false;
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && style.display !== 'none';
    };

    if (window.matchMedia('(max-width: 749px)').matches) {
      const logoRow = document.querySelector('#header-component .header__row--top');
      const logoBottom = isVisible(logoRow) ? logoRow.getBoundingClientRect().bottom : 0;
      this.style.setProperty('--ocs-cart-toast-top', `${Math.max(Math.round(logoBottom + 4), 4)}px`);
      this.style.removeProperty('--ocs-cart-toast-right');
      return;
    }

    const chip = document.querySelector('ocs-cart-chip-component');
    const chipVisible = isVisible(chip);
    const chipRow = chip?.closest?.('.header__row');
    const headerRows = [...document.querySelectorAll('#header-component .header__row')].filter(isVisible);
    const fallbackRow = headerRows.reduce((lowestRow, row) => {
      if (!lowestRow) return row;
      return row.getBoundingClientRect().bottom > lowestRow.getBoundingClientRect().bottom ? row : lowestRow;
    }, null);
    const anchorRow = isVisible(chipRow) ? chipRow : fallbackRow;
    const topAnchor = chipVisible ? chip : anchorRow;

    if (!topAnchor) {
      this.style.removeProperty('--ocs-cart-toast-top');
    } else {
      const top = Math.max(Math.round(topAnchor.getBoundingClientRect().bottom + 4), 4);
      this.style.setProperty('--ocs-cart-toast-top', `${top}px`);
    }

    if (chipVisible) {
      const chipRect = chip.getBoundingClientRect();
      const right = Math.max(Math.round(window.innerWidth - chipRect.right), 8);
      this.style.setProperty('--ocs-cart-toast-right', `${right}px`);
    } else {
      this.style.removeProperty('--ocs-cart-toast-right');
    }
  };

  #clearTimer() {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('ocs-cart-toast-component')) {
  customElements.define('ocs-cart-toast-component', OcsCartToastComponent);
}
